/**
 * PGS Achievement Bridge — two-layer sync strategy.
 *
 * Layer 1: Real-time quiz sync (standard unlocks only, per-quiz)
 *   - Only fires unlockAchievement() for 3 standard achievements
 *   - Never calls incrementAchievement()
 *   - Never touches sync cursor or outbox
 *
 * Layer 2: Sync-on-init catch-up (authoritative, on app start)
 *   - Delta-based incremental sync with outbox pattern
 *   - Standard achievement recovery from user_achievements
 *   - Mutex-guarded, at-least-once delivery
 */

import { Platform } from 'react-native';

import { supabase } from '../../lib/supabase';
import type { PGSQuizSyncParams } from '../types/playGames';

import {
  PGS_INCREMENTAL_ACHIEVEMENTS,
  PGS_STANDARD_ACHIEVEMENTS,
} from './pgsAchievementMap';
import * as playGamesService from './playGamesService';

// ─── Layer 1: Real-time quiz sync ───────────────────────────────────────

/**
 * Called immediately after quiz completion. Fire-and-forget.
 * Only unlocks standard achievements derivable from the quiz payload.
 * Never increments. Never touches cursor/outbox.
 */
export async function syncQuizToPGS(params: PGSQuizSyncParams): Promise<void> {
  if (Platform.OS !== 'android' || !playGamesService.isPGSAvailable()) return;

  try {
    const { correctCount, totalQuestions, gameType, rank } = params;

    // perfect_score: all answers correct
    if (correctCount === totalQuestions) {
      const pgsId = PGS_STANDARD_ACHIEVEMENTS.perfect_score?.pgsId;
      if (pgsId) {
        playGamesService.unlockAchievement(pgsId).catch(() => {});
      }
    }

    // multiplayer_first: completed any MP quiz
    if (gameType === 'multiplayer') {
      const pgsId = PGS_STANDARD_ACHIEVEMENTS.multiplayer_first?.pgsId;
      if (pgsId) {
        playGamesService.unlockAchievement(pgsId).catch(() => {});
      }
    }

    // multiplayer_winner: rank 1 in MP
    if (gameType === 'multiplayer' && rank === 1) {
      const pgsId = PGS_STANDARD_ACHIEVEMENTS.multiplayer_winner?.pgsId;
      if (pgsId) {
        playGamesService.unlockAchievement(pgsId).catch(() => {});
      }
    }
  } catch (e) {
    // Fire-and-forget — never throw, never block quiz flow
    console.warn('[PGS Bridge] syncQuizToPGS error:', e);
  }
}

// ─── Layer 2: Sync-on-init catch-up ─────────────────────────────────────

let isSyncing = false;

/**
 * Full state sync from Supabase to PGS. Called on app init after
 * silent PGS auth succeeds AND existing mapping is confirmed.
 * Mutex-guarded — concurrent calls silently dropped.
 */
export async function syncFullStateToPGS(userId: string): Promise<void> {
  if (Platform.OS !== 'android' || !playGamesService.isPGSAvailable()) return;
  if (isSyncing) return; // mutex

  isSyncing = true;
  try {
    // Step 2: Read all needed data in parallel
    const [
      aggregateResult,
      historyResult,
      syncStateResult,
    ] = await Promise.all([
      fetchAggregateData(userId),
      fetchHistoryData(userId),
      fetchSyncState(userId),
    ]);

    if (!aggregateResult || !historyResult || !syncStateResult) {
      console.warn('[PGS Bridge] syncFullStateToPGS: failed to fetch data');
      return;
    }

    const { userXP, userStats, userAchievements } = aggregateResult;
    const { spCount, mpCount, spPerfectCount, mpPerfectCount, spCategories, mpCategories, mpFinishedCount, languageCount } = historyResult;
    const { cursors, unlockLog, outbox } = syncStateResult;

    // Step 3: Retry pending outbox entries first
    for (const entry of outbox) {
      const pgsId = PGS_INCREMENTAL_ACHIEVEMENTS[entry.achievement_key]?.pgsId;
      if (!pgsId) continue;
      try {
        await playGamesService.incrementAchievement(pgsId, entry.delta);
        // Success: delete outbox row, advance cursor
        await supabase.from('pgs_sync_outbox').delete().eq('id', entry.id);
        await upsertCursor(userId, entry.achievement_key, entry.target_cursor);
      } catch {
        // Leave for next session
      }
    }

    // Step 4: Incremental achievements (outbox pattern)
    const incrementalSources: Record<string, number> = {
      quiz_10: spCount + mpCount,
      quiz_50: spCount + mpCount,
      quiz_100: spCount + mpCount,
      quiz_500: spCount + mpCount,
      knowledge_100: userStats?.correct_answered ?? 0,
      knowledge_1000: userStats?.correct_answered ?? 0,
      perfect_10: spPerfectCount + mpPerfectCount,
      streak_30: userStats?.longest_streak ?? 0,
      streak_100: userStats?.longest_streak ?? 0,
      explorer_5: new Set([...spCategories, ...mpCategories]).size,
      explorer_20: new Set([...spCategories, ...mpCategories]).size,
      multiplayer_10: mpFinishedCount,
      level_10: userXP?.current_level ?? 0,
      level_25: userXP?.current_level ?? 0,
      polyglot_3: languageCount,
    };

    for (const [key, currentValue] of Object.entries(incrementalSources)) {
      const achievement = PGS_INCREMENTAL_ACHIEVEMENTS[key];
      if (!achievement) continue;

      const lastSynced = cursors[key] ?? 0;
      const delta = currentValue - lastSynced;

      if (delta <= 0) continue; // skip if no progress or admin correction

      // Write outbox row (idempotent via UNIQUE constraint)
      const { error: outboxError } = await supabase
        .from('pgs_sync_outbox')
        .upsert(
          {
            user_id: userId,
            achievement_key: key,
            delta,
            target_cursor: currentValue,
          },
          { onConflict: 'user_id,achievement_key,target_cursor', ignoreDuplicates: true }
        );

      if (outboxError) {
        console.warn(`[PGS Bridge] outbox write failed for ${key}:`, outboxError.message);
        continue;
      }

      try {
        await playGamesService.incrementAchievement(achievement.pgsId, delta);
        // Success: delete outbox row, advance cursor
        await supabase
          .from('pgs_sync_outbox')
          .delete()
          .eq('user_id', userId)
          .eq('achievement_key', key)
          .eq('target_cursor', currentValue);
        await upsertCursor(userId, key, currentValue);
      } catch {
        // Outbox row survives — retried next session
      }
    }

    // Step 5: Standard achievements
    const standardSources: Record<string, () => boolean> = {
      first_quiz: () => (spCount + mpCount) >= 1,
      perfect_score: () => userAchievements.some(a => a.achievement_key === 'perfect_score' && a.completed),
      truth_seeker: () => userAchievements.some(a => a.achievement_key === 'truth_seeker' && a.completed),
      binary_master: () => userAchievements.some(a => a.achievement_key === 'binary_master' && a.completed),
      lightning_judge: () => userAchievements.some(a => a.achievement_key === 'lightning_judge' && a.completed),
      discernment_expert: () => userAchievements.some(a => a.achievement_key === 'discernment_expert' && a.completed),
      wisdom_seeker: () => userAchievements.some(a => a.achievement_key === 'wisdom_seeker' && a.completed),
      multiplayer_first: () => userAchievements.some(a => a.achievement_key === 'multiplayer_first' && a.completed),
      multiplayer_winner: () => userAchievements.some(a => a.achievement_key === 'multiplayer_winner' && a.completed),
      xp_1000: () => (userXP?.total_xp ?? 0) >= 1000,
      xp_10000: () => (userXP?.total_xp ?? 0) >= 10000,
      scholar_rank: () => userXP?.rank === 'Scholar',
      streak_7: () => (userStats?.longest_streak ?? 0) >= 7,
    };

    const alreadyUnlocked = new Set(unlockLog.map(u => u.achievement_key));

    for (const [key, check] of Object.entries(standardSources)) {
      if (alreadyUnlocked.has(key)) continue; // already synced
      if (!check()) continue; // condition not met

      const pgsId = PGS_STANDARD_ACHIEVEMENTS[key]?.pgsId;
      if (!pgsId) continue;

      try {
        await playGamesService.unlockAchievement(pgsId);
        // Log successful unlock
        await supabase.from('pgs_standard_unlock_log').upsert(
          { user_id: userId, achievement_key: key, synced_at: new Date().toISOString() },
          { onConflict: 'user_id,achievement_key' }
        );
      } catch {
        // No log row → retried next session
      }
    }

    // Step 6: Update last_synced_at
    await supabase
      .from('pgs_identity_map')
      .update({ last_synced_at: new Date().toISOString() })
      .eq('user_id', userId);
  } catch (e) {
    console.warn('[PGS Bridge] syncFullStateToPGS error:', e);
  } finally {
    isSyncing = false;
  }
}

// ─── Fallback detection ─────────────────────────────────────────────────

/**
 * Returns true if batchProcessGamification returned a fallback/synthetic result.
 * PGS sync is skipped entirely on fallback results.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFallbackResult(result: { afterXP?: { id?: string } } & Record<string, any>): boolean {
  return result?.afterXP?.id === 'fallback';
}

// ─── Internal helpers ───────────────────────────────────────────────────

async function fetchAggregateData(userId: string) {
  try {
    const [xpRes, statsRes, achieveRes] = await Promise.all([
      supabase.from('user_xp').select('total_xp, current_level, rank').eq('user_id', userId).maybeSingle(),
      supabase.from('user_stats').select('longest_streak, correct_answered').eq('user_id', userId).maybeSingle(),
      supabase.from('user_achievements').select('completed, achievement:achievements!inner(achievement_key)').eq('user_id', userId),
    ]);
    return {
      userXP: xpRes.data as { total_xp: number; current_level: number; rank: string } | null,
      userStats: statsRes.data as { longest_streak: number; correct_answered: number } | null,
      // PostgREST types a to-one join as an array; it is a single object at runtime.
      userAchievements: ((achieveRes.data ?? []) as unknown as Array<{ completed: boolean; achievement: { achievement_key: string } | null }>)
        .map(row => ({ achievement_key: row.achievement?.achievement_key ?? '', completed: row.completed })),
    };
  } catch {
    return null;
  }
}

async function fetchHistoryData(userId: string) {
  try {
    const [spRes, mpRes, , langRes] = await Promise.all([
      supabase.from('singleplayer_results').select('category_id, score, total_questions').eq('user_id', userId),
      supabase.from('multiplayer_game_room_players').select('room_id, score').eq('user_id', userId).not('finished_at', 'is', null),
      // Placeholder — MP categories fetched separately below from room_ids
      Promise.resolve(null),
      supabase.from('singleplayer_results').select('language').eq('user_id', userId),
    ]);

    const spResults = spRes.data ?? [];
    const mpResults = mpRes.data ?? [];

    // Get unique MP room IDs to look up categories
    const mpRoomIds = [...new Set(mpResults.map(r => r.room_id))];
    let mpCategories: number[] = [];
    if (mpRoomIds.length > 0) {
      const { data: rooms } = await supabase
        .from('multiplayer_game_rooms')
        .select('id, category_id')
        .in('id', mpRoomIds);
      mpCategories = [...new Set((rooms ?? []).map(r => r.category_id))];
    }

    // SP perfect scores: score === total_questions
    const spPerfectCount = spResults.filter(r => r.score === r.total_questions).length;
    // MP perfect scores: score === 10 (MP is always 10 questions, score = raw correct)
    const mpPerfectCount = mpResults.filter(r => r.score === 10).length;

    // Unique languages
    const languages = new Set((langRes.data ?? []).map(r => r.language).filter(Boolean));

    return {
      spCount: spResults.length,
      mpCount: mpResults.length,
      spPerfectCount,
      mpPerfectCount,
      spCategories: [...new Set(spResults.map(r => r.category_id))],
      mpCategories,
      mpFinishedCount: mpResults.length,
      languageCount: languages.size,
    };
  } catch {
    return null;
  }
}

async function fetchSyncState(userId: string) {
  try {
    const [cursorRes, unlockRes, outboxRes] = await Promise.all([
      supabase.from('pgs_achievement_sync_cursor').select('achievement_key, last_synced_value').eq('user_id', userId),
      supabase.from('pgs_standard_unlock_log').select('achievement_key').eq('user_id', userId),
      supabase.from('pgs_sync_outbox').select('*').eq('user_id', userId),
    ]);

    const cursors: Record<string, number> = {};
    for (const row of cursorRes.data ?? []) {
      cursors[row.achievement_key] = row.last_synced_value;
    }

    return {
      cursors,
      unlockLog: (unlockRes.data ?? []) as Array<{ achievement_key: string }>,
      outbox: (outboxRes.data ?? []) as Array<{
        id: string;
        achievement_key: string;
        delta: number;
        target_cursor: number;
      }>,
    };
  } catch {
    return null;
  }
}

async function upsertCursor(userId: string, key: string, value: number) {
  await supabase.from('pgs_achievement_sync_cursor').upsert(
    {
      user_id: userId,
      achievement_key: key,
      last_synced_value: value,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,achievement_key' }
  );
}
